import React, { useState } from 'react';
import { ChevronRight, ChevronLeft, Check, Users, Eye, Heart, AlertTriangle, Save, Loader2, Scissors, Clock, Pill, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// ==================== STEP 1: ANAMNESI FAMILIARE ====================

// Family conditions - Patologie Sistemiche
const FAMILY_SYSTEMIC_CONDITIONS = [
  { id: 'diabete', label: 'Diabete' },
  { id: 'ipertensione', label: 'Ipertensione' },
  { id: 'infarto_ictus', label: 'Infarto / Ictus' },
  { id: 'neoplasie', label: 'Neoplasie / Melanomi' },
  { id: 'ipercolesterolemia', label: 'Ipercolesterolemia' },
  { id: 'iperplasia_prostatica', label: 'Iperplasia Prostatica Benigna' },
  { id: 'malattie_autoimmuni', label: 'Malattie Autoimmuni' },
  { id: 'malattie_tiroidee', label: 'Malattie Tiroidee' }
];

// Family conditions - Patologie Oculari
const FAMILY_OCULAR_CONDITIONS = [
  { id: 'glaucoma', label: 'Glaucoma' },
  { id: 'cataratta_congenita', label: 'Cataratta congenita' },
  { id: 'miopia_elevata', label: 'Miopia elevata (> 6 diottrie)' },
  { id: 'ambliopia', label: 'Ambliopia (occhio pigro)' },
  { id: 'strabismo', label: 'Strabismo' },
  { id: 'cheratocono', label: 'Cheratocono' },
  { id: 'maculopatia', label: 'Maculopatia' },
  { id: 'retinite_pigmentosa', label: 'Retinite Pigmentosa' },
  { id: 'distacco_retina', label: 'Distacco di Retina' },
  { id: 'uveiti', label: 'Uveiti' }
];

// Relatives
const RELATIVES = [
  { id: 'madre', label: 'Madre' },
  { id: 'padre', label: 'Padre' },
  { id: 'fratello', label: 'Fratello' },
  { id: 'sorella', label: 'Sorella' },
  { id: 'nonno_materno', label: 'Nonno materno' },
  { id: 'nonna_materna', label: 'Nonna materna' },
  { id: 'nonno_paterno', label: 'Nonno paterno' },
  { id: 'nonna_paterna', label: 'Nonna paterna' },
  { id: 'zio', label: 'Zio/a' },
  { id: 'cugino', label: 'Cugino/a' },
  { id: 'figlio', label: 'Figlio/a' },
  { id: 'altro', label: 'Altro parente' }
];

// ==================== STEP 2: ANAMNESI OCULARE PERSONALE ====================

const OCULAR_CATEGORIES = [
  {
    id: 'vizi_refrattivi',
    title: 'Vizi Refrattivi',
    icon: '👓',
    color: '#3B82F6',
    conditions: [
      { id: 'miopia', label: 'Miopia' },
      { id: 'ipermetropia', label: 'Ipermetropia' },
      { id: 'astigmatismo', label: 'Astigmatismo' },
      { id: 'presbiopia', label: 'Presbiopia' },
      { id: 'anisometropia', label: 'Anisometropia' },
      { id: 'ambliopia', label: 'Ambliopia (occhio pigro)' }
    ]
  },
  {
    id: 'chirurgia_refrattiva',
    title: 'Chirurgia Refrattiva',
    icon: '✂️',
    color: '#8B5CF6',
    conditions: [
      { id: 'lasik', label: 'LASIK' },
      { id: 'prk', label: 'PRK / LASEK' },
      { id: 'smile', label: 'SMILE' },
      { id: 'icl', label: 'Lenti ICL' },
      { id: 'cheratotomia', label: 'Cheratotomia radiale' }
    ]
  },
  {
    id: 'strabismo_ambliopia',
    title: 'Strabismo / Ambliopia',
    icon: '👁️',
    color: '#EC4899',
    conditions: [
      { id: 'strabismo', label: 'Strabismo' },
      { id: 'diplopia', label: 'Diplopia (visione doppia)' },
      { id: 'ambliopia', label: 'Ambliopia (occhio pigro)' },
      { id: 'intervento_strabismo', label: 'Intervento per strabismo' }
    ]
  },
  {
    id: 'superficie_oculare',
    title: 'Superficie Oculare',
    icon: '💧',
    color: '#06B6D4',
    conditions: [
      { id: 'occhio_secco', label: 'Sindrome occhio secco' },
      { id: 'blefarite', label: 'Blefarite' },
      { id: 'congiuntivite_allergica', label: 'Congiuntivite allergica' },
      { id: 'congiuntivite_cronica', label: 'Congiuntivite cronica' },
      { id: 'pterigio', label: 'Pterigio' },
      { id: 'pinguecola', label: 'Pinguecola' }
    ]
  },
  {
    id: 'cheratocono',
    title: 'Cheratocono',
    icon: '🔵',
    color: '#0EA5E9',
    conditions: [
      { id: 'cheratocono', label: 'Cheratocono' },
      { id: 'cross_linking', label: 'Cross-linking effettuato' },
      { id: 'anelli_intrastromali', label: 'Anelli intrastromali' },
      { id: 'lenti_rigide', label: 'Uso lenti rigide gas-permeabili' }
    ]
  },
  {
    id: 'patologie_corneali',
    title: 'Altre Patologie Corneali',
    icon: '🟢',
    color: '#10B981',
    conditions: [
      { id: 'guttata', label: 'Guttata endoteliale (Fuchs)' },
      { id: 'herpes_corneale', label: 'Herpes corneale' },
      { id: 'distrofie_corneali', label: 'Distrofie corneali' },
      { id: 'ulcera_corneale', label: 'Ulcera corneale' },
      { id: 'trapianto_cornea', label: 'Trapianto di cornea' }
    ]
  },
  {
    id: 'cataratta',
    title: 'Cataratta',
    icon: '⚪',
    color: '#6B7280',
    conditions: [
      { id: 'cataratta', label: 'Cataratta' },
      { id: 'cataratta_congenita', label: 'Cataratta congenita' },
      { id: 'intervento_cataratta', label: 'Intervento di cataratta' },
      { id: 'cataratta_secondaria', label: 'Cataratta secondaria' },
      { id: 'laser_yag', label: 'Capsulotomia YAG Laser' }
    ]
  },
  {
    id: 'glaucoma',
    title: 'Glaucoma',
    icon: '🔴',
    color: '#EF4444',
    conditions: [
      { id: 'glaucoma_cronico', label: 'Glaucoma cronico ad angolo aperto' },
      { id: 'glaucoma_angolo_chiuso', label: 'Glaucoma ad angolo chiuso' },
      { id: 'glaucoma_normoteso', label: 'Glaucoma normoteso' },
      { id: 'ipertono_oculare', label: 'Ipertono oculare' },
      { id: 'attacco_acuto', label: 'Attacco acuto di glaucoma' },
      { id: 'iridotomia', label: 'Iridotomia laser' },
      { id: 'trabeculoplastica', label: 'Trabeculoplastica laser' },
      { id: 'intervento_glaucoma', label: 'Intervento chirurgico per glaucoma' }
    ]
  },
  {
    id: 'uveiti',
    title: 'Uveiti',
    icon: '🟠',
    color: '#F97316',
    conditions: [
      { id: 'uveite_anteriore', label: 'Uveite anteriore' },
      { id: 'uveite_intermedia', label: 'Uveite intermedia' },
      { id: 'uveite_posteriore', label: 'Uveite posteriore' },
      { id: 'panuveite', label: 'Panuveite' }
    ]
  },
  {
    id: 'maculopatie',
    title: 'Maculopatie',
    icon: '🟡',
    color: '#EAB308',
    conditions: [
      { id: 'dmle_secca', label: 'DMLE secca (atrofica)' },
      { id: 'dmle_umida', label: 'DMLE umida (essudativa)' },
      { id: 'maculopatia_miopica', label: 'Maculopatia miopica' },
      { id: 'foro_maculare', label: 'Foro maculare' },
      { id: 'pucker_maculare', label: 'Pucker maculare' },
      { id: 'edema_maculare', label: 'Edema maculare' },
      { id: 'iniezioni_intravitreali', label: 'Iniezioni intravitreali' }
    ]
  },
  {
    id: 'retinopatia_diabetica',
    title: 'Retinopatia Diabetica',
    icon: '🩸',
    color: '#DC2626',
    conditions: [
      { id: 'rd_non_proliferante_lieve', label: 'Non proliferante lieve' },
      { id: 'rd_non_proliferante_moderata', label: 'Non proliferante moderata' },
      { id: 'rd_non_proliferante_grave', label: 'Non proliferante grave' },
      { id: 'rd_proliferante', label: 'Proliferante' },
      { id: 'edema_maculare_diabetico', label: 'Edema maculare diabetico' },
      { id: 'laser_retinopatia', label: 'Trattamento laser' },
      { id: 'vitrectomia_diabetica', label: 'Vitrectomia' }
    ]
  },
  {
    id: 'patologie_retiniche',
    title: 'Altre Patologie Retiniche',
    icon: '🔶',
    color: '#F59E0B',
    conditions: [
      { id: 'distacco_retina', label: 'Distacco di retina' },
      { id: 'rotture_retiniche', label: 'Rotture/fori retinici' },
      { id: 'degenerazione_lattice', label: 'Degenerazione a lattice' },
      { id: 'csc', label: 'Corioretinopatia Sierosa Centrale' },
      { id: 'occlusione_venosa', label: 'Occlusione venosa retinica' },
      { id: 'occlusione_arteriosa', label: 'Occlusione arteriosa retinica' },
      { id: 'laser_retinico', label: 'Laser retinico (barrage)' },
      { id: 'vitrectomia', label: 'Vitrectomia' },
      { id: 'cerchiaggio', label: 'Cerchiaggio/piombaggio' }
    ]
  },
  {
    id: 'traumi',
    title: 'Traumi Oculari',
    icon: '⚡',
    color: '#7C3AED',
    conditions: [
      { id: 'trauma_contusivo', label: 'Trauma contusivo' },
      { id: 'trauma_perforante', label: 'Trauma perforante' },
      { id: 'corpo_estraneo', label: 'Corpo estraneo oculare' },
      { id: 'ustione_chimica', label: 'Ustione chimica' },
      { id: 'ustione_termica', label: 'Ustione termica' }
    ]
  }
];

// ==================== STEP 3: USO CORREZIONI OTTICHE ====================

const CORRECTION_OPTIONS = {
  occhiali: {
    title: 'Occhiali',
    icon: '👓',
    options: [
      { id: 'occhiali_vista', label: 'Occhiali da vista' },
      { id: 'occhiali_lettura', label: 'Solo per lettura' },
      { id: 'occhiali_progressivi', label: 'Lenti progressive' },
      { id: 'occhiali_sole_graduati', label: 'Occhiali da sole graduati' }
    ]
  },
  lenti_contatto: {
    title: 'Lenti a Contatto',
    icon: '👁️',
    options: [
      { id: 'lac_morbide_giornaliere', label: 'Morbide giornaliere' },
      { id: 'lac_morbide_mensili', label: 'Morbide mensili' },
      { id: 'lac_morbide_toriche', label: 'Morbide toriche' },
      { id: 'lac_rigide', label: 'Rigide gas-permeabili' },
      { id: 'lac_multifocali', label: 'Multifocali' },
      { id: 'lac_ortocheratologia', label: 'Ortocheratologia (notturne)' }
    ]
  }
};

// ==================== SINTOMI OCULARI ====================

const OCULAR_SYMPTOMS = [
  // ===== CATEGORIA: ACUITÀ VISIVA (difficoltà a vedere) =====
  { 
    id: 'difficolta_lontano', 
    label: 'Difficoltà a vedere da lontano', 
    description: '👤 Come lo descrivono i pazienti: "Non riesco più a leggere i cartelli stradali", "Vedo sfocato da lontano", "Devo strizzare gli occhi per vedere la TV".\n\n📋 Cosa significa: Difficoltà a mettere a fuoco oggetti distanti. Potrebbe indicare miopia o altri difetti visivi.',
    category: 'acuita'
  },
  { 
    id: 'difficolta_vicino', 
    label: 'Difficoltà a vedere da vicino', 
    description: '👤 Come lo descrivono i pazienti: "Devo allontanare il telefono per leggere", "Non riesco più a leggere le etichette", "Per leggere devo allungare le braccia".\n\n📋 Cosa significa: Difficoltà a mettere a fuoco oggetti vicini, tipico della presbiopia (comune dopo i 40-45 anni).',
    category: 'acuita'
  },
  { 
    id: 'visione_offuscata', 
    label: 'Visione offuscata/annebbiata', 
    description: '👤 Come lo descrivono i pazienti: "Vedo come attraverso un vetro appannato", "Ho sempre la sensazione di avere qualcosa sugli occhi", "La vista è nebbiosa".\n\n📋 Cosa significa: La visione appare velata o poco nitida. Può essere costante o intermittente.',
    category: 'acuita'
  },
  { 
    id: 'visione_doppia', 
    label: 'Visione doppia (Diplopia)', 
    description: '👤 Come lo descrivono i pazienti: "Vedo due immagini sovrapposte", "Gli oggetti sembrano sdoppiati", "Ho le immagini che si sovrappongono".\n\n📋 Cosa significa: Si vedono due immagini dello stesso oggetto. Può essere monoculare (con un occhio) o binoculare (con entrambi).',
    category: 'acuita'
  },

  // ===== CATEGORIA: DISTURBI DELLA VISIONE =====
  { 
    id: 'emeralopia', 
    label: 'Emeralopia (difficoltà al crepuscolo)', 
    description: '👤 Come lo descrivono i pazienti: "Quando inizia a fare buio non vedo bene", "Al tramonto faccio fatica", "Ho difficoltà a vedere nelle giornate nuvolose".\n\n📋 Cosa significa: Difficoltà ad adattarsi alla scarsa luminosità, tipica del crepuscolo. Diversa dalla cecità notturna completa.',
    category: 'visione'
  },
  { 
    id: 'nictalopia', 
    label: 'Nictalopia (cecità notturna)', 
    description: '👤 Come lo descrivono i pazienti: "Di notte non vedo quasi nulla", "Al buio sono completamente perso", "Quando spengo la luce non vedo più niente".\n\n📋 Cosa significa: Grave difficoltà o incapacità di vedere al buio. L\'occhio non si adatta all\'oscurità.',
    category: 'visione'
  },
  { 
    id: 'fotofobia', 
    label: 'Fotofobia (fastidio alla luce)', 
    description: '👤 Come lo descrivono i pazienti: "La luce mi dà fastidio", "Devo sempre portare gli occhiali da sole", "Il sole mi fa male agli occhi", "Con le luci forti devo chiudere gli occhi".\n\n📋 Cosa significa: Sensibilità eccessiva alla luce che causa fastidio o dolore.',
    category: 'visione'
  },
  { 
    id: 'abbagliamento', 
    label: 'Abbagliamento', 
    description: '👤 Come lo descrivono i pazienti: "I fari delle auto mi abbagliano", "Ho difficoltà con il sole frontale", "Le luci mi danno molto fastidio e non vedo bene".\n\n📋 Cosa significa: Difficoltà visiva causata da fonti di luce intensa che riducono la capacità di vedere.',
    category: 'visione'
  },
  { 
    id: 'aloni', 
    label: 'Aloni intorno alle luci', 
    description: '👤 Come lo descrivono i pazienti: "Vedo dei cerchi intorno ai lampioni", "Le luci hanno un alone attorno", "I fari delle auto hanno dei raggi/cerchi".\n\n📋 Cosa significa: Percezione di cerchi luminosi che circondano le fonti di luce. Comune di notte.',
    category: 'visione'
  },
  { 
    id: 'mosche_volanti', 
    label: 'Miodesopsie (mosche volanti)', 
    description: '👤 Come lo descrivono i pazienti: "Vedo delle mosche che volano", "Ho dei puntini che si muovono", "Vedo dei filamenti/ragnatele", "Ci sono delle ombre che seguono lo sguardo".\n\n📋 Cosa significa: Percezione di puntini, filamenti o ombre nel campo visivo che si muovono con lo sguardo. Spesso più visibili su sfondi chiari.',
    category: 'visione'
  },
  { 
    id: 'lampi_luce', 
    label: 'Fotopsie (lampi di luce)', 
    description: '👤 Come lo descrivono i pazienti: "Vedo dei flash di luce", "Ho dei lampi improvvisi", "Vedo delle luci anche al buio", "Ho degli scintillii".\n\n📋 Cosa significa: Percezione di lampi, flash o scintillii anche in assenza di luce reale. ⚠️ Se improvvisi e frequenti, consultare urgentemente.',
    category: 'visione'
  },
  { 
    id: 'macchia_centrale', 
    label: 'Scotoma (macchia nel campo visivo)', 
    description: '👤 Come lo descrivono i pazienti: "Ho una macchia scura al centro", "C\'è un\'area dove non vedo", "Ho un buco nella visione", "Vedo una zona nera".\n\n📋 Cosa significa: Area del campo visivo dove la vista è ridotta o assente. Può essere centrale o periferico.',
    category: 'visione'
  },
  { 
    id: 'visione_tunnel', 
    label: 'Visione a tunnel (restringimento campo visivo)', 
    description: '👤 Come lo descrivono i pazienti: "Vedo come attraverso un tubo", "Non vedo ai lati", "Ho perso la visione laterale", "Sbatto contro le cose ai lati".\n\n📋 Cosa significa: Perdita della visione periferica, si vede bene solo al centro. Tipico del glaucoma avanzato.',
    category: 'visione'
  },
  { 
    id: 'metamorfopsie', 
    label: 'Metamorfopsie (immagini distorte)', 
    description: '👤 Come lo descrivono i pazienti: "Le linee dritte sembrano storte", "Le righe del quaderno sono ondulate", "I bordi delle porte/finestre sono deformati", "I volti delle persone sembrano distorti".\n\n📋 Cosa significa: Le immagini appaiono deformate, ondulate o distorte. Le linee rette sembrano curve. Tipico di problemi alla macula (parte centrale della retina). ⚠️ Sintomo importante da segnalare.',
    category: 'visione'
  },
  { 
    id: 'difficolta_profondita', 
    label: 'Difficoltà percezione profondità/3D', 
    description: '👤 Come lo descrivono i pazienti: "Non riesco a valutare le distanze", "Ho difficoltà a parcheggiare", "Non vedo bene il 3D al cinema", "Faccio fatica a versare l\'acqua nel bicchiere".\n\n📋 Cosa significa: Difficoltà a percepire la profondità e le distanze tra gli oggetti. Può indicare problemi di visione binoculare.',
    category: 'visione'
  },
  { 
    id: 'difficolta_gradini', 
    label: 'Difficoltà con scale/gradini', 
    description: '👤 Come lo descrivono i pazienti: "Ho paura di scendere le scale", "Non vedo bene i gradini", "Devo guardare sempre dove metto i piedi", "Mi sento insicuro sulle scale".\n\n📋 Cosa significa: Difficoltà a percepire l\'altezza dei gradini, spesso legata a problemi di percezione della profondità o del campo visivo inferiore.',
    category: 'visione'
  },

  // ===== CATEGORIA: COMFORT E SUPERFICIE OCULARE =====
  { 
    id: 'lacrimazione', 
    label: 'Lacrimazione eccessiva', 
    description: '👤 Come lo descrivono i pazienti: "Mi lacrimano sempre gli occhi", "Ho gli occhi che piangono", "Le lacrime mi scendono senza motivo", "Devo sempre asciugarmi gli occhi".\n\n📋 Cosa significa: Produzione eccessiva di lacrime o difficoltà nel drenaggio. Paradossalmente può essere causata anche da occhio secco.',
    category: 'comfort'
  },
  { 
    id: 'secchezza', 
    label: 'Secchezza oculare', 
    description: '👤 Come lo descrivono i pazienti: "Ho gli occhi secchi", "Sento gli occhi che tirano", "Mi sembra di avere gli occhi aridi", "Ho bisogno continuo di lacrime artificiali".\n\n📋 Cosa significa: Sensazione di occhi asciutti, spesso peggiora con aria condizionata, vento, o uso prolungato di schermi.',
    category: 'comfort'
  },
  { 
    id: 'corpo_estraneo', 
    label: 'Sensazione di corpo estraneo', 
    description: '👤 Come lo descrivono i pazienti: "Sento come se avessi sabbia negli occhi", "Ho qualcosa nell\'occhio ma non c\'è niente", "Sento come della polvere", "Ho una sensazione di granelli".\n\n📋 Cosa significa: Sensazione di avere qualcosa nell\'occhio anche quando non c\'è. Spesso legato a secchezza o irritazione.',
    category: 'comfort'
  },
  { 
    id: 'bruciore', 
    label: 'Bruciore oculare', 
    description: '👤 Come lo descrivono i pazienti: "Mi bruciano gli occhi", "Ho una sensazione di calore", "Gli occhi mi pizzicano", "Sento come un fuoco negli occhi".\n\n📋 Cosa significa: Sensazione di bruciore o calore agli occhi. Comune con secchezza, allergie o affaticamento.',
    category: 'comfort'
  },
  { 
    id: 'prurito', 
    label: 'Prurito oculare', 
    description: '👤 Come lo descrivono i pazienti: "Mi prudono gli occhi", "Ho sempre voglia di strofinarmi gli occhi", "Ho un fastidio che mi fa grattare".\n\n📋 Cosa significa: Necessità di strofinare gli occhi. Tipico di allergie oculari o blefarite.',
    category: 'comfort'
  },
  { 
    id: 'dolore_oculare', 
    label: 'Dolore oculare', 
    description: '👤 Come lo descrivono i pazienti: "Mi fa male l\'occhio", "Ho un dolore dentro l\'occhio", "Sento una fitta", "Ho male intorno all\'occhio".\n\n📋 Cosa significa: Dolore localizzato nell\'occhio o nella zona circostante. ⚠️ Se intenso o improvviso, consultare urgentemente.',
    category: 'comfort'
  },
  { 
    id: 'dolore_retrobulbare', 
    label: 'Dolore dietro l\'occhio', 
    description: '👤 Come lo descrivono i pazienti: "Ho male dietro l\'occhio", "Sento una pressione dietro", "Mi fa male quando muovo l\'occhio", "Ho dolore profondo nell\'orbita".\n\n📋 Cosa significa: Dolore percepito nella parte posteriore dell\'occhio (retrobulbare). Può essere associato a sinusite, neurite ottica o altre condizioni. ⚠️ Se accompagnato da calo visivo, consultare urgentemente.',
    category: 'comfort'
  },
  { 
    id: 'dolore_movimento', 
    label: 'Dolore al movimento degli occhi', 
    description: '👤 Come lo descrivono i pazienti: "Mi fa male quando guardo in alto/basso/lateralmente", "Muovere gli occhi mi provoca dolore", "Ho fastidio quando sposto lo sguardo".\n\n📋 Cosa significa: Dolore che si manifesta durante i movimenti oculari. Può indicare infiammazione dei muscoli oculari o del nervo ottico.',
    category: 'comfort'
  },
  { 
    id: 'cefalea_occhio', 
    label: 'Mal di testa intorno all\'occhio', 
    description: '👤 Come lo descrivono i pazienti: "Ho mal di testa sopra l\'occhio", "Mi fa male la fronte vicino all\'occhio", "Ho dolore alla tempia e all\'occhio", "Sento pulsare vicino all\'occhio".\n\n📋 Cosa significa: Cefalea localizzata nella zona perioculare. Può essere tensiva, da affaticamento visivo, o correlata a problemi oculari.',
    category: 'comfort'
  },
  { 
    id: 'pesantezza_palpebre', 
    label: 'Pesantezza/stanchezza palpebre', 
    description: '👤 Come lo descrivono i pazienti: "Ho le palpebre pesanti", "Faccio fatica a tenere gli occhi aperti", "Mi cadono le palpebre", "Sento gli occhi stanchi".\n\n📋 Cosa significa: Sensazione di palpebre affaticate, spesso associata a stanchezza generale o affaticamento visivo.',
    category: 'comfort'
  },
  { 
    id: 'pesantezza_occhi', 
    label: 'Sensazione di pesantezza sugli occhi', 
    description: '👤 Come lo descrivono i pazienti: "Sento gli occhi pesanti", "Ho come un peso sugli occhi", "Gli occhi mi sembrano stanchi e appesantiti", "Sento una pressione sugli occhi", "Ho gli occhi affaticati e pesanti".\n\n📋 Cosa significa: Sensazione di peso o pressione sugli occhi, diversa dalla pesantezza delle palpebre. Spesso associata ad affaticamento visivo, stress, mancanza di sonno o uso prolungato di schermi.',
    category: 'comfort'
  },
  { 
    id: 'arrossamento', 
    label: 'Arrossamento oculare', 
    description: '👤 Come lo descrivono i pazienti: "Ho gli occhi rossi", "Si vedono le venuzze", "Ho l\'occhio iniettato di sangue", "Gli occhi sono sempre arrossati".\n\n📋 Cosa significa: Occhi rossi con vasi sanguigni visibili. Può essere da irritazione, allergia, infezione o altro.',
    category: 'comfort'
  },
  { 
    id: 'gonfiore_palpebre', 
    label: 'Gonfiore palpebre', 
    description: '👤 Come lo descrivono i pazienti: "Ho le palpebre gonfie", "Mi sveglio con gli occhi gonfi", "Ho un rigonfiamento sulla palpebra".\n\n📋 Cosa significa: Palpebre ingrossate, può essere da allergia, infezione, ritenzione idrica.',
    category: 'comfort'
  },
  { 
    id: 'secrezioni', 
    label: 'Secrezioni/croste', 
    description: '👤 Come lo descrivono i pazienti: "Ho le ciglia appiccicate al mattino", "Mi sveglio con gli occhi incollati", "Ho delle croste sulle ciglia", "Esce del muco dall\'occhio".\n\n📋 Cosa significa: Presenza di muco, croste o secrezioni. Può indicare blefarite, congiuntivite o infezione.',
    category: 'comfort'
  },

  // ===== CATEGORIA: AFFATICAMENTO VISIVO =====
  { 
    id: 'affaticamento_visivo', 
    label: 'Affaticamento visivo generale', 
    description: '👤 Come lo descrivono i pazienti: "Ho gli occhi stanchi", "Dopo un po\' di lavoro non ce la faccio più", "Sento gli occhi affaticati", "Devo fare pause frequenti".\n\n📋 Cosa significa: Stanchezza oculare dopo attività visiva prolungata (lettura, computer, TV).',
    category: 'affaticamento'
  },
  { 
    id: 'cefalea_visiva', 
    label: 'Mal di testa da sforzo visivo', 
    description: '👤 Come lo descrivono i pazienti: "Mi viene mal di testa quando leggo", "Dopo il computer ho la cefalea", "Ho dolore alla fronte quando guardo", "Mi fa male la testa dietro gli occhi".\n\n📋 Cosa significa: Mal di testa che compare durante o dopo attività visiva. Spesso frontale o intorno agli occhi.',
    category: 'affaticamento'
  },
  { 
    id: 'mal_di_testa', 
    label: 'Mal di testa frequente', 
    description: '👤 Come lo descrivono i pazienti:\n\n🔹 SENZA AURA: "Mi viene mal di testa spesso", "Ho la testa che pulsa", "Sento una morsa alla testa", "Ho dolore da un lato della testa", "Mi fa male la nuca".\n\n🔹 CON AURA (prima del mal di testa): "Prima di avere mal di testa vedo delle lucine", "Vedo dei zig-zag luminosi", "Ho delle macchie nella vista prima dell\'emicrania", "Vedo come un arcobaleno/scintillio", "La vista si annebbia e poi arriva il mal di testa".\n\n📋 Cosa significa:\n• Mal di testa SENZA aura: cefalea comune, può essere tensiva o emicranica\n• Mal di testa CON aura: emicrania preceduta da disturbi visivi (luci, zig-zag, macchie) che durano 15-60 minuti. L\'aura visiva è un segnale importante da riferire al medico.',
    category: 'affaticamento'
  },
  { 
    id: 'emicrania_oftalmica', 
    label: 'Emicrania con disturbi visivi (aura)', 
    description: '👤 Come lo descrivono i pazienti: "Vedo delle luci a zig-zag prima del mal di testa", "Mi compare uno scintillio che si allarga", "Vedo come dei fulmini/lampi colorati", "Ho una macchia nella vista che cresce e poi sparisce", "Vedo un arco luminoso che tremola".\n\n📋 Cosa significa: L\'AURA VISIVA è un disturbo temporaneo della vista che precede l\'emicrania. Tipicamente:\n• Dura 15-60 minuti\n• Luci scintillanti a zig-zag (spettro di fortificazione)\n• Macchia cieca che si espande\n• Dopo l\'aura arriva il mal di testa\n\n⚠️ Se l\'aura dura più di 60 minuti o non è seguita da mal di testa, segnalarlo al medico.',
    category: 'affaticamento'
  },
  { 
    id: 'difficolta_lettura', 
    label: 'Difficoltà nella lettura', 
    description: '👤 Come lo descrivono i pazienti: "Faccio fatica a leggere", "Le lettere si confondono", "Perdo il rigo", "Dopo poco non riesco più a leggere".\n\n📋 Cosa significa: Difficoltà a leggere testi, può essere da presbiopia, affaticamento o altro difetto visivo.',
    category: 'affaticamento'
  },
  { 
    id: 'difficolta_computer', 
    label: 'Difficoltà al computer/smartphone', 
    description: '👤 Come lo descrivono i pazienti: "Dopo un po\' di computer non vedo più", "Ho fastidio con gli schermi", "Il telefono mi stanca gli occhi", "Non riesco a stare molto al PC".\n\n📋 Cosa significa: Affaticamento visivo specifico con dispositivi digitali. La sindrome da visione al computer è molto comune.',
    category: 'affaticamento'
  },
  { 
    id: 'difficolta_guida_notturna', 
    label: 'Difficoltà nella guida notturna', 
    description: '👤 Come lo descrivono i pazienti: "Non mi sento sicuro a guidare di notte", "I fari mi abbagliano troppo", "Di notte non vedo bene la strada", "Evito di guidare quando è buio".\n\n📋 Cosa significa: Difficoltà a guidare in condizioni di scarsa illuminazione. Può essere da cataratta iniziale, problemi di adattamento al buio o abbagliamento.',
    category: 'affaticamento'
  }
];

// ==================== STEP 4: ANAMNESI GENERALE ====================

const SYSTEMIC_CONDITIONS = [
  { id: 'diabete_tipo1', label: 'Diabete Tipo 1' },
  { id: 'diabete_tipo2', label: 'Diabete Tipo 2' },
  { id: 'ipertensione', label: 'Ipertensione arteriosa' },
  { id: 'cardiopatia', label: 'Cardiopatia' },
  { id: 'ictus', label: 'Ictus / TIA' },
  { id: 'artrite_reumatoide', label: 'Artrite reumatoide' },
  { id: 'lupus', label: 'Lupus eritematoso' },
  { id: 'sjogren', label: 'Sindrome di Sjögren' },
  { id: 'sclerosi_multipla', label: 'Sclerosi multipla' },
  { id: 'malattie_tiroidee', label: 'Malattie tiroidee' },
  { id: 'hiv', label: 'HIV / AIDS' },
  { id: 'epatite', label: 'Epatite B / C' },
  { id: 'neoplasie', label: 'Neoplasie / Tumori' }
];

const GuidedAnamnesis = ({ onComplete, onCancel }) => {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  
  // Step 1: Family history
  const [familyHistory, setFamilyHistory] = useState({});
  
  // Step 2: Personal ocular pathologies (with OD/OS)
  const [ocularPathologies, setOcularPathologies] = useState({});
  const [interventiChirurgici, setInterventiChirurgici] = useState('');
  
  // Step 3: Optical corrections
  const [corrections, setCorrections] = useState({
    occhiali: [],
    lenti_contatto: [],
    anni_uso_lac: '',
    ore_giorno_lac: '',
    problemi_lac: ''
  });
  
  // Step 4: General anamnesis
  const [generalAnamnesis, setGeneralAnamnesis] = useState({
    systemic_conditions: [],
    allergie_farmaci: '',
    allergie_colliri: '',
    farmaci_sistemici: '',
    farmaci_oculari: '',
    gravidanza: null,
    allattamento: null,
    nato_prematuro: null,
    ultima_visita: '',
    motivo_visita: '',
    note_paziente: ''
  });
  
  // Step 5: Symptoms
  const [symptoms, setSymptoms] = useState([]);
  const [symptomNotes, setSymptomNotes] = useState('');

  const totalSteps = 5;

  // Toggle family condition
  const toggleFamilyCondition = (conditionId) => {
    setFamilyHistory(prev => {
      if (prev[conditionId]) {
        const newState = { ...prev };
        delete newState[conditionId];
        return newState;
      }
      return { ...prev, [conditionId]: { selected: true, relatives: [] } };
    });
  };

  // Toggle relative for a condition
  const toggleRelative = (conditionId, relativeId) => {
    setFamilyHistory(prev => {
      const current = prev[conditionId]?.relatives || [];
      const newRelatives = current.includes(relativeId)
        ? current.filter(r => r !== relativeId)
        : [...current, relativeId];
      return {
        ...prev,
        [conditionId]: { ...prev[conditionId], relatives: newRelatives }
      };
    });
  };

  // Toggle ocular pathology (with OD/OS)
  const toggleOcularPathology = (pathologyId, eye) => {
    setOcularPathologies(prev => {
      const current = prev[pathologyId] || { od: false, os: false };
      return {
        ...prev,
        [pathologyId]: { ...current, [eye]: !current[eye] }
      };
    });
  };

  // Toggle correction
  const toggleCorrection = (category, optionId) => {
    setCorrections(prev => {
      const current = prev[category] || [];
      const newList = current.includes(optionId)
        ? current.filter(o => o !== optionId)
        : [...current, optionId];
      return { ...prev, [category]: newList };
    });
  };

  // Toggle systemic condition
  const toggleSystemicCondition = (conditionId) => {
    setGeneralAnamnesis(prev => {
      const current = prev.systemic_conditions || [];
      const newList = current.includes(conditionId)
        ? current.filter(c => c !== conditionId)
        : [...current, conditionId];
      return { ...prev, systemic_conditions: newList };
    });
  };

  // Toggle symptom
  const toggleSymptom = (symptomId) => {
    setSymptoms(prev => {
      if (prev.includes(symptomId)) {
        return prev.filter(s => s !== symptomId);
      }
      return [...prev, symptomId];
    });
  };

  // Check if any pathology in a category is selected
  const hasCategorySelection = (categoryId) => {
    const category = OCULAR_CATEGORIES.find(c => c.id === categoryId);
    if (!category) return false;
    return category.conditions.some(c => {
      const pathology = ocularPathologies[c.id];
      return pathology?.od || pathology?.os;
    });
  };

  // Build report text
  const buildReportText = () => {
    let report = '📋 ANAMNESI OCULISTICA COMPLETA\n';
    report += `📅 Data: ${new Date().toLocaleDateString('it-IT')}\n\n`;
    
    // Family history
    report += '═══════════════════════════════════════\n';
    report += '👪 ANAMNESI FAMILIARE\n';
    report += '═══════════════════════════════════════\n\n';
    
    const allFamilyConditions = [...FAMILY_SYSTEMIC_CONDITIONS, ...FAMILY_OCULAR_CONDITIONS];
    const selectedFamily = Object.entries(familyHistory).filter(([_, v]) => v.selected);
    
    if (selectedFamily.length === 0) {
      report += '• Nessuna patologia familiare significativa\n';
    } else {
      report += '【Patologie Sistemiche】\n';
      selectedFamily.filter(([id]) => FAMILY_SYSTEMIC_CONDITIONS.find(c => c.id === id)).forEach(([condId, data]) => {
        const condition = allFamilyConditions.find(c => c.id === condId);
        const relatives = data.relatives.map(r => RELATIVES.find(rel => rel.id === r)?.label).filter(Boolean).join(', ');
        report += `  ▸ ${condition?.label}: ${relatives || 'parente non specificato'}\n`;
      });
      
      report += '\n【Patologie Oculari】\n';
      selectedFamily.filter(([id]) => FAMILY_OCULAR_CONDITIONS.find(c => c.id === id)).forEach(([condId, data]) => {
        const condition = allFamilyConditions.find(c => c.id === condId);
        const relatives = data.relatives.map(r => RELATIVES.find(rel => rel.id === r)?.label).filter(Boolean).join(', ');
        report += `  ▸ ${condition?.label}: ${relatives || 'parente non specificato'}\n`;
      });
    }
    
    // Personal ocular history
    report += '\n═══════════════════════════════════════\n';
    report += '👁️ ANAMNESI OCULARE PERSONALE\n';
    report += '═══════════════════════════════════════\n\n';
    
    let hasOcularPathologies = false;
    OCULAR_CATEGORIES.forEach(category => {
      const selectedInCategory = category.conditions.filter(c => {
        const p = ocularPathologies[c.id];
        return p?.od || p?.os;
      });
      if (selectedInCategory.length > 0) {
        hasOcularPathologies = true;
        report += `【${category.title}】\n`;
        selectedInCategory.forEach(c => {
          const p = ocularPathologies[c.id];
          const eyes = [];
          if (p?.od) eyes.push('OD');
          if (p?.os) eyes.push('OS');
          report += `  ▸ ${c.label} (${eyes.join(' + ')})\n`;
        });
        report += '\n';
      }
    });
    if (!hasOcularPathologies) {
      report += '• Nessuna patologia oculare pregressa\n\n';
    }

    // Interventi chirurgici oculistici
    if (interventiChirurgici && interventiChirurgici.trim()) {
      report += '【Interventi Chirurgici Oculistici Pregressi】\n';
      report += `  ${interventiChirurgici.trim()}\n\n`;
    }
    
    // Optical corrections
    report += '═══════════════════════════════════════\n';
    report += '👓 CORREZIONE OTTICA\n';
    report += '═══════════════════════════════════════\n\n';
    
    if (corrections.occhiali.length > 0) {
      report += '【Occhiali】\n';
      corrections.occhiali.forEach(id => {
        const opt = CORRECTION_OPTIONS.occhiali.options.find(o => o.id === id);
        if (opt) report += `  ▸ ${opt.label}\n`;
      });
      report += '\n';
    }
    
    if (corrections.lenti_contatto.length > 0) {
      report += '【Lenti a Contatto】\n';
      corrections.lenti_contatto.forEach(id => {
        const opt = CORRECTION_OPTIONS.lenti_contatto.options.find(o => o.id === id);
        if (opt) report += `  ▸ ${opt.label}\n`;
      });
      if (corrections.anni_uso_lac) report += `  • Anni di utilizzo: ${corrections.anni_uso_lac}\n`;
      if (corrections.ore_giorno_lac) report += `  • Ore al giorno: ${corrections.ore_giorno_lac}\n`;
      if (corrections.problemi_lac) report += `  • Problemi riscontrati: ${corrections.problemi_lac}\n`;
      report += '\n';
    }
    
    if (corrections.occhiali.length === 0 && corrections.lenti_contatto.length === 0) {
      report += '• Non utilizza correzioni ottiche\n\n';
    }
    
    // General anamnesis
    report += '═══════════════════════════════════════\n';
    report += '🏥 ANAMNESI GENERALE\n';
    report += '═══════════════════════════════════════\n\n';
    
    if (generalAnamnesis.systemic_conditions.length > 0) {
      report += '【Patologie Sistemiche】\n';
      generalAnamnesis.systemic_conditions.forEach(id => {
        const cond = SYSTEMIC_CONDITIONS.find(c => c.id === id);
        if (cond) report += `  ▸ ${cond.label}\n`;
      });
      report += '\n';
    }
    
    if (generalAnamnesis.allergie_farmaci || generalAnamnesis.allergie_colliri) {
      report += '【Allergie】\n';
      if (generalAnamnesis.allergie_farmaci) report += `  ▸ Farmaci: ${generalAnamnesis.allergie_farmaci}\n`;
      if (generalAnamnesis.allergie_colliri) report += `  ▸ Colliri: ${generalAnamnesis.allergie_colliri}\n`;
      report += '\n';
    }
    
    if (generalAnamnesis.farmaci_sistemici || generalAnamnesis.farmaci_oculari) {
      report += '【Terapie in Corso】\n';
      if (generalAnamnesis.farmaci_sistemici) report += `  ▸ Farmaci sistemici: ${generalAnamnesis.farmaci_sistemici}\n`;
      if (generalAnamnesis.farmaci_oculari) report += `  ▸ Colliri/farmaci oculari: ${generalAnamnesis.farmaci_oculari}\n`;
      report += '\n';
    }
    
    report += '【Altre Informazioni】\n';
    if (generalAnamnesis.gravidanza === true) report += `  ▸ Gravidanza in corso: Sì\n`;
    if (generalAnamnesis.allattamento === true) report += `  ▸ Allattamento: Sì\n`;
    if (generalAnamnesis.nato_prematuro === true) report += `  ▸ Nato prematuro: Sì\n`;
    if (generalAnamnesis.ultima_visita) report += `  ▸ Ultima visita oculistica: ${generalAnamnesis.ultima_visita}\n`;
    
    if (generalAnamnesis.motivo_visita) {
      report += `\n【Motivo della Visita】\n  ${generalAnamnesis.motivo_visita}\n`;
    }
    
    // Symptoms section
    if (symptoms.length > 0) {
      report += '\n═══════════════════════════════════════\n';
      report += '🩺 SINTOMI RIFERITI DAL PAZIENTE\n';
      report += '═══════════════════════════════════════\n\n';
      
      const visionSymptoms = symptoms.filter(s => OCULAR_SYMPTOMS.find(os => os.id === s)?.category === 'visione');
      const comfortSymptoms = symptoms.filter(s => OCULAR_SYMPTOMS.find(os => os.id === s)?.category === 'comfort');
      const fatigueSymptoms = symptoms.filter(s => OCULAR_SYMPTOMS.find(os => os.id === s)?.category === 'affaticamento');
      
      if (visionSymptoms.length > 0) {
        report += '【Disturbi della Visione】\n';
        visionSymptoms.forEach(s => {
          const symptom = OCULAR_SYMPTOMS.find(os => os.id === s);
          if (symptom) report += `  ▸ ${symptom.label}\n`;
        });
        report += '\n';
      }
      
      if (comfortSymptoms.length > 0) {
        report += '【Sintomi di Comfort/Superficie】\n';
        comfortSymptoms.forEach(s => {
          const symptom = OCULAR_SYMPTOMS.find(os => os.id === s);
          if (symptom) report += `  ▸ ${symptom.label}\n`;
        });
        report += '\n';
      }
      
      if (fatigueSymptoms.length > 0) {
        report += '【Affaticamento Visivo】\n';
        fatigueSymptoms.forEach(s => {
          const symptom = OCULAR_SYMPTOMS.find(os => os.id === s);
          if (symptom) report += `  ▸ ${symptom.label}\n`;
        });
        report += '\n';
      }
      
      if (symptomNotes) {
        report += `【Dettagli sui sintomi】\n  ${symptomNotes}\n`;
      }
    }
    
    if (generalAnamnesis.note_paziente) {
      report += `\n═══════════════════════════════════════\n`;
      report += `📝 IL PAZIENTE AGGIUNGE\n`;
      report += `═══════════════════════════════════════\n\n`;
      report += `${generalAnamnesis.note_paziente}\n`;
    }
    
    return report;
  };

  // Build critical tags
  const buildCriticalTags = () => {
    const tags = [];
    
    // Family history tags
    if (familyHistory.glaucoma?.selected) tags.push('⚠️ FAMILIARITÀ GLAUCOMA');
    if (familyHistory.maculopatia?.selected) tags.push('⚠️ FAMILIARITÀ MACULOPATIA');
    if (familyHistory.distacco_retina?.selected) tags.push('⚠️ FAMILIARITÀ DISTACCO RETINA');
    if (familyHistory.diabete?.selected) tags.push('⚠️ FAMILIARITÀ DIABETE');
    if (familyHistory.cheratocono?.selected) tags.push('⚠️ FAMILIARITÀ CHERATOCONO');
    
    // Personal pathology tags
    if (ocularPathologies.glaucoma_cronico?.od || ocularPathologies.glaucoma_cronico?.os) tags.push('🔴 GLAUCOMA');
    if (ocularPathologies.rd_proliferante?.od || ocularPathologies.rd_proliferante?.os) tags.push('🔴 RETINOPATIA PROLIFERANTE');
    if (ocularPathologies.distacco_retina?.od || ocularPathologies.distacco_retina?.os) tags.push('🔴 PREGRESSO DISTACCO RETINA');
    if (ocularPathologies.cheratocono?.od || ocularPathologies.cheratocono?.os) tags.push('🟡 CHERATOCONO');
    if (ocularPathologies.dmle_umida?.od || ocularPathologies.dmle_umida?.os) tags.push('🔴 DMLE UMIDA');
    
    // Allergy tags
    if (generalAnamnesis.allergie_farmaci) tags.push('⚠️ ALLERGIE FARMACI');
    if (generalAnamnesis.allergie_colliri) tags.push('⚠️ ALLERGIE COLLIRI');
    
    // Systemic tags
    if (generalAnamnesis.systemic_conditions.includes('diabete_tipo1') || 
        generalAnamnesis.systemic_conditions.includes('diabete_tipo2')) {
      tags.push('🩸 DIABETE');
    }
    
    if (generalAnamnesis.gravidanza === true) tags.push('🤰 GRAVIDANZA');
    
    return tags;
  };

  // Save anamnesis
  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem('token');
    
    const reportText = buildReportText();
    const criticalTags = buildCriticalTags();
    
    const structuredData = {
      family_history: familyHistory,
      ocular_pathologies: ocularPathologies,
      interventi_chirurgici: interventiChirurgici,
      corrections: corrections,
      general_anamnesis: generalAnamnesis,
      symptoms: symptoms,
      symptom_notes: symptomNotes,
      completed_at: new Date().toISOString()
    };

    try {
      const response = await fetch(`${API}/api/chat/save-anamnesis`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          session_id: `guided_${Date.now()}`,
          report_text: reportText,
          structured_data: structuredData,
          critical_tags: criticalTags
        })
      });

      if (response.ok) {
        toast.success('Anamnesi salvata con successo!');
        if (onComplete) onComplete();
      } else {
        throw new Error('Errore nel salvataggio');
      }
    } catch (error) {
      console.error('Error saving anamnesis:', error);
      toast.error('Errore nel salvataggio. Riprova.');
    } finally {
      setSaving(false);
    }
  };

  // Render step indicator
  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-4">
      {[1, 2, 3, 4, 5].map((s, idx) => (
        <React.Fragment key={s}>
          <div 
            className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
              step === s 
                ? 'bg-[#2A9FCB] text-white shadow-lg' 
                : step > s 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gray-200 text-gray-500'
            }`}
          >
            {step > s ? <Check className="w-4 h-4" /> : s}
          </div>
          {idx < 4 && (
            <div className={`w-8 h-1 mx-1 rounded ${step > s ? 'bg-green-500' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  // Step titles
  const stepTitles = [
    { icon: Users, title: 'Anamnesi Familiare', subtitle: 'Patologie in famiglia' },
    { icon: Eye, title: 'Patologie Oculari', subtitle: 'La tua storia oculare' },
    { icon: Scissors, title: 'Correzioni Ottiche', subtitle: 'Occhiali e lenti a contatto' },
    { icon: Heart, title: 'Anamnesi Generale', subtitle: 'Salute e farmaci' },
    { icon: AlertTriangle, title: 'Sintomi Attuali', subtitle: 'Cosa avverti ora' }
  ];

  // ==================== RENDER STEP 1 ====================
  // Check if any systemic family condition is selected
  const hasSystemicFamilySelection = () => {
    return FAMILY_SYSTEMIC_CONDITIONS.some(c => familyHistory[c.id]?.selected);
  };

  // Check if any ocular family condition is selected
  const hasOcularFamilySelection = () => {
    return FAMILY_OCULAR_CONDITIONS.some(c => familyHistory[c.id]?.selected);
  };

  const renderStep1 = () => (
    <div className="space-y-4 max-h-[420px] overflow-y-auto pr-1">
      <p className="text-sm text-center text-gray-500 mb-3">
        Seleziona le patologie presenti in famiglia e indica quale parente ne è affetto
      </p>

      {/* Systemic conditions in family - Card style like Step 2 */}
      <div 
        className="rounded-xl overflow-hidden border transition-all"
        style={{ borderColor: hasSystemicFamilySelection() ? '#EF4444' : '#E5E7EB' }}
      >
        <div 
          className="px-3 py-2.5 flex items-center gap-2"
          style={{ background: hasSystemicFamilySelection() ? '#FEE2E2' : '#F9FAFB' }}
        >
          <Heart className="w-4 h-4" style={{ color: '#EF4444' }} />
          <h3 className="font-semibold text-sm" style={{ color: '#EF4444' }}>Patologie Sistemiche in Famiglia</h3>
          {hasSystemicFamilySelection() && (
            <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-red-500 text-white">
              {FAMILY_SYSTEMIC_CONDITIONS.filter(c => familyHistory[c.id]?.selected).length} selezionate
            </span>
          )}
        </div>
        <div className="p-3 bg-white">
          <div className="grid grid-cols-2 gap-2">
            {FAMILY_SYSTEMIC_CONDITIONS.map(condition => (
              <div key={condition.id}>
                <button
                  onClick={() => toggleFamilyCondition(condition.id)}
                  className={`w-full p-2.5 rounded-lg text-left text-sm font-medium transition-all ${
                    familyHistory[condition.id]?.selected
                      ? 'bg-red-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {condition.label}
                </button>
                
                {familyHistory[condition.id]?.selected && (
                  <div className="mt-2 p-2.5 rounded-lg bg-red-50 border border-red-200">
                    <p className="text-xs font-medium text-red-600 mb-2">Quale parente?</p>
                    <div className="flex flex-wrap gap-1">
                      {RELATIVES.map(rel => (
                        <button
                          key={rel.id}
                          onClick={() => toggleRelative(condition.id, rel.id)}
                          className={`px-2 py-1 rounded text-xs transition-all ${
                            familyHistory[condition.id]?.relatives?.includes(rel.id)
                              ? 'bg-red-500 text-white'
                              : 'bg-white text-gray-600 border border-gray-200 hover:border-red-400'
                          }`}
                        >
                          {rel.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Ocular conditions in family - Card style like Step 2 */}
      <div 
        className="rounded-xl overflow-hidden border transition-all"
        style={{ borderColor: hasOcularFamilySelection() ? '#3B82F6' : '#E5E7EB' }}
      >
        <div 
          className="px-3 py-2.5 flex items-center gap-2"
          style={{ background: hasOcularFamilySelection() ? '#DBEAFE' : '#F9FAFB' }}
        >
          <Eye className="w-4 h-4" style={{ color: '#3B82F6' }} />
          <h3 className="font-semibold text-sm" style={{ color: '#3B82F6' }}>Patologie Oculari in Famiglia</h3>
          {hasOcularFamilySelection() && (
            <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-blue-500 text-white">
              {FAMILY_OCULAR_CONDITIONS.filter(c => familyHistory[c.id]?.selected).length} selezionate
            </span>
          )}
        </div>
        <div className="p-3 bg-white">
          <div className="grid grid-cols-2 gap-2">
            {FAMILY_OCULAR_CONDITIONS.map(condition => (
              <div key={condition.id}>
                <button
                  onClick={() => toggleFamilyCondition(condition.id)}
                  className={`w-full p-2.5 rounded-lg text-left text-sm font-medium transition-all ${
                    familyHistory[condition.id]?.selected
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {condition.label}
                </button>
                
                {familyHistory[condition.id]?.selected && (
                  <div className="mt-2 p-2.5 rounded-lg bg-blue-50 border border-blue-200">
                    <p className="text-xs font-medium text-blue-600 mb-2">Quale parente?</p>
                    <div className="flex flex-wrap gap-1">
                      {RELATIVES.map(rel => (
                        <button
                          key={rel.id}
                          onClick={() => toggleRelative(condition.id, rel.id)}
                          className={`px-2 py-1 rounded text-xs transition-all ${
                            familyHistory[condition.id]?.relatives?.includes(rel.id)
                              ? 'bg-blue-500 text-white'
                              : 'bg-white text-gray-600 border border-gray-200 hover:border-blue-400'
                          }`}
                        >
                          {rel.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // ==================== RENDER STEP 2 ====================
  const renderStep2 = () => (
    <div className="space-y-3">
      <p className="text-sm text-center text-gray-500 mb-3">
        Seleziona le patologie di cui soffri o hai sofferto (OD = Destro, OS = Sinistro)
      </p>

      <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
        {OCULAR_CATEGORIES.map(category => (
          <div 
            key={category.id} 
            className="rounded-xl overflow-hidden border transition-all"
            style={{ borderColor: hasCategorySelection(category.id) ? category.color : '#E5E7EB' }}
          >
            <div 
              className="px-3 py-2 flex items-center gap-2"
              style={{ background: hasCategorySelection(category.id) ? `${category.color}15` : '#F9FAFB' }}
            >
              <span className="text-base">{category.icon}</span>
              <h3 className="font-semibold text-sm" style={{ color: category.color }}>{category.title}</h3>
            </div>
            <div className="p-3 bg-white">
              {category.conditions.map(condition => {
                const pathology = ocularPathologies[condition.id] || { od: false, os: false };
                return (
                  <div key={condition.id} className="flex items-center justify-between py-1.5 border-b last:border-0" style={{ borderColor: '#F3F4F6' }}>
                    <span className="text-sm text-gray-700 flex-1 pr-2">{condition.label}</span>
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => toggleOcularPathology(condition.id, 'od')}
                        className={`px-2.5 py-1 rounded text-xs font-bold transition-all ${
                          pathology.od
                            ? 'text-white'
                            : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                        }`}
                        style={pathology.od ? { background: '#2A9FCB' } : {}}
                      >
                        OD
                      </button>
                      <button
                        onClick={() => toggleOcularPathology(condition.id, 'os')}
                        className={`px-2.5 py-1 rounded text-xs font-bold transition-all ${
                          pathology.os
                            ? 'text-white'
                            : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                        }`}
                        style={pathology.os ? { background: '#F59E0B' } : {}}
                      >
                        OS
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Interventi Chirurgici Oculistici Pregressi */}
      <div className="rounded-xl p-4" style={{ background: '#FEF3C7', border: '1px solid #F59E0B' }}>
        <h3 className="text-sm font-semibold mb-2 flex items-center gap-2" style={{ color: '#92400E' }}>
          🔪 Interventi Chirurgici Oculistici Pregressi
        </h3>
        <p className="text-xs text-amber-700 mb-2">
          Indicare tipo di intervento e anno (es: "Intervento di cataratta occhio destro - 2020")
        </p>
        <textarea
          value={interventiChirurgici}
          onChange={(e) => setInterventiChirurgici(e.target.value)}
          placeholder="Es: Intervento di cataratta OD nel 2020, Laser retinico OS nel 2018..."
          className="w-full p-3 rounded-lg border text-sm resize-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
          style={{ borderColor: '#E5E7EB', minHeight: '70px' }}
          rows={2}
        />
      </div>
    </div>
  );

  // ==================== RENDER STEP 3 ====================
  const renderStep3 = () => (
    <div className="space-y-5">
      <p className="text-sm text-center text-gray-500 mb-4">
        Indica quali correzioni ottiche utilizzi attualmente
      </p>

      {/* Occhiali */}
      <div className="rounded-xl p-4" style={{ background: '#F9FAFB', border: '1px solid #E5E7EB' }}>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          👓 Occhiali
        </h3>
        <div className="grid grid-cols-2 gap-2">
          {CORRECTION_OPTIONS.occhiali.options.map(opt => (
            <button
              key={opt.id}
              onClick={() => toggleCorrection('occhiali', opt.id)}
              className={`p-2.5 rounded-lg text-sm font-medium transition-all text-left ${
                corrections.occhiali.includes(opt.id)
                  ? 'bg-[#2A9FCB] text-white'
                  : 'bg-white text-gray-700 border border-gray-200 hover:border-[#2A9FCB]'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Lenti a contatto */}
      <div className="rounded-xl p-4" style={{ background: '#F9FAFB', border: '1px solid #E5E7EB' }}>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          👁️ Lenti a Contatto
        </h3>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {CORRECTION_OPTIONS.lenti_contatto.options.map(opt => (
            <button
              key={opt.id}
              onClick={() => toggleCorrection('lenti_contatto', opt.id)}
              className={`p-2.5 rounded-lg text-sm font-medium transition-all text-left ${
                corrections.lenti_contatto.includes(opt.id)
                  ? 'bg-[#2A9FCB] text-white'
                  : 'bg-white text-gray-700 border border-gray-200 hover:border-[#2A9FCB]'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        
        {corrections.lenti_contatto.length > 0 && (
          <div className="space-y-3 pt-3 border-t" style={{ borderColor: '#E5E7EB' }}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Da quanti anni le usi?</label>
                <input
                  type="text"
                  value={corrections.anni_uso_lac}
                  onChange={(e) => setCorrections(prev => ({ ...prev, anni_uso_lac: e.target.value }))}
                  placeholder="Es: 5 anni"
                  className="w-full p-2 rounded-lg border text-sm"
                  style={{ borderColor: '#E5E7EB' }}
                />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Ore al giorno?</label>
                <input
                  type="text"
                  value={corrections.ore_giorno_lac}
                  onChange={(e) => setCorrections(prev => ({ ...prev, ore_giorno_lac: e.target.value }))}
                  placeholder="Es: 8-10 ore"
                  className="w-full p-2 rounded-lg border text-sm"
                  style={{ borderColor: '#E5E7EB' }}
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Hai avuto problemi con le LAC?</label>
              <input
                type="text"
                value={corrections.problemi_lac}
                onChange={(e) => setCorrections(prev => ({ ...prev, problemi_lac: e.target.value }))}
                placeholder="Es: occhio secco, infezioni, intolleranza..."
                className="w-full p-2 rounded-lg border text-sm"
                style={{ borderColor: '#E5E7EB' }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ==================== RENDER STEP 4 ====================
  const renderStep4 = () => (
    <div className="space-y-4 max-h-[420px] overflow-y-auto pr-1">
      <p className="text-sm text-center text-gray-500 mb-3">
        Informazioni sulla tua salute generale
      </p>

      {/* Patologie sistemiche */}
      <div className="rounded-xl p-4" style={{ background: '#F9FAFB', border: '1px solid #E5E7EB' }}>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Heart className="w-4 h-4 text-red-500" /> Soffri di queste patologie?
        </h3>
        <div className="grid grid-cols-2 gap-2">
          {SYSTEMIC_CONDITIONS.map(cond => (
            <button
              key={cond.id}
              onClick={() => toggleSystemicCondition(cond.id)}
              className={`p-2 rounded-lg text-xs font-medium transition-all text-left ${
                generalAnamnesis.systemic_conditions.includes(cond.id)
                  ? 'bg-[#2A9FCB] text-white'
                  : 'bg-white text-gray-700 border border-gray-200 hover:border-[#2A9FCB]'
              }`}
            >
              {cond.label}
            </button>
          ))}
        </div>
      </div>

      {/* Allergie */}
      <div className="rounded-xl p-4" style={{ background: '#FEF3C7', border: '1px solid #F59E0B' }}>
        <h3 className="text-sm font-semibold text-amber-800 mb-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> Allergie
        </h3>
        <div className="grid grid-cols-1 gap-3">
          <div>
            <label className="text-xs text-amber-700 mb-1 block">Allergie a farmaci</label>
            <input
              type="text"
              value={generalAnamnesis.allergie_farmaci}
              onChange={(e) => setGeneralAnamnesis(prev => ({ ...prev, allergie_farmaci: e.target.value }))}
              placeholder="Es: Penicillina, FANS, Sulfamidici..."
              className="w-full p-2 rounded-lg border text-sm"
              style={{ borderColor: '#E5E7EB' }}
            />
          </div>
          <div>
            <label className="text-xs text-amber-700 mb-1 block">Allergie a colliri / anestetici</label>
            <input
              type="text"
              value={generalAnamnesis.allergie_colliri}
              onChange={(e) => setGeneralAnamnesis(prev => ({ ...prev, allergie_colliri: e.target.value }))}
              placeholder="Es: Benoxinato, Tropicamide..."
              className="w-full p-2 rounded-lg border text-sm"
              style={{ borderColor: '#E5E7EB' }}
            />
          </div>
        </div>
      </div>

      {/* Terapie */}
      <div className="rounded-xl p-4" style={{ background: '#E8F4FD', border: '1px solid #2A9FCB' }}>
        <h3 className="text-sm font-semibold text-[#2A9FCB] mb-3 flex items-center gap-2">
          <Pill className="w-4 h-4" /> Terapie in Corso
        </h3>
        <div className="grid grid-cols-1 gap-3">
          <div>
            <label className="text-xs text-[#2A9FCB] mb-1 block">Farmaci sistemici</label>
            <input
              type="text"
              value={generalAnamnesis.farmaci_sistemici}
              onChange={(e) => setGeneralAnamnesis(prev => ({ ...prev, farmaci_sistemici: e.target.value }))}
              placeholder="Es: Antiipertensivi, Antidiabetici, Anticoagulanti..."
              className="w-full p-2 rounded-lg border text-sm"
              style={{ borderColor: '#E5E7EB' }}
            />
          </div>
          <div>
            <label className="text-xs text-[#2A9FCB] mb-1 block">Colliri / Terapie oculari</label>
            <input
              type="text"
              value={generalAnamnesis.farmaci_oculari}
              onChange={(e) => setGeneralAnamnesis(prev => ({ ...prev, farmaci_oculari: e.target.value }))}
              placeholder="Es: Colliri antiglaucoma, Lacrime artificiali..."
              className="w-full p-2 rounded-lg border text-sm"
              style={{ borderColor: '#E5E7EB' }}
            />
          </div>
        </div>
      </div>

      {/* Altre info */}
      <div className="rounded-xl p-4" style={{ background: '#F9FAFB', border: '1px solid #E5E7EB' }}>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Altre Informazioni</h3>
        
        <div className="space-y-3">
          {/* Gravidanza/Allattamento */}
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="text-xs text-gray-600 mb-1 block">Gravidanza in corso?</label>
              <div className="flex gap-2">
                {[{ v: true, l: 'Sì' }, { v: false, l: 'No' }, { v: null, l: 'N/A' }].map(o => (
                  <button
                    key={String(o.v)}
                    onClick={() => setGeneralAnamnesis(prev => ({ ...prev, gravidanza: o.v }))}
                    className={`flex-1 py-1.5 rounded text-xs font-medium ${
                      generalAnamnesis.gravidanza === o.v ? 'bg-[#2A9FCB] text-white' : 'bg-white border'
                    }`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex-1">
              <label className="text-xs text-gray-600 mb-1 block">Allattamento?</label>
              <div className="flex gap-2">
                {[{ v: true, l: 'Sì' }, { v: false, l: 'No' }, { v: null, l: 'N/A' }].map(o => (
                  <button
                    key={String(o.v)}
                    onClick={() => setGeneralAnamnesis(prev => ({ ...prev, allattamento: o.v }))}
                    className={`flex-1 py-1.5 rounded text-xs font-medium ${
                      generalAnamnesis.allattamento === o.v ? 'bg-[#2A9FCB] text-white' : 'bg-white border'
                    }`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Nato prematuro */}
          <div>
            <label className="text-xs text-gray-600 mb-1 block">Nato prematuro (&lt;32 settimane)?</label>
            <div className="flex gap-2">
              {[{ v: true, l: 'Sì' }, { v: false, l: 'No' }, { v: null, l: 'Non so' }].map(o => (
                <button
                  key={String(o.v)}
                  onClick={() => setGeneralAnamnesis(prev => ({ ...prev, nato_prematuro: o.v }))}
                  className={`flex-1 py-1.5 rounded text-xs font-medium ${
                    generalAnamnesis.nato_prematuro === o.v ? 'bg-[#2A9FCB] text-white' : 'bg-white border'
                  }`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>

          {/* Ultima visita */}
          <div>
            <label className="text-xs text-gray-600 mb-1 block">Ultima visita oculistica</label>
            <input
              type="text"
              value={generalAnamnesis.ultima_visita}
              onChange={(e) => setGeneralAnamnesis(prev => ({ ...prev, ultima_visita: e.target.value }))}
              placeholder="Es: 6 mesi fa, Mai, 2023..."
              className="w-full p-2 rounded-lg border text-sm"
              style={{ borderColor: '#E5E7EB' }}
            />
          </div>

          {/* Motivo visita */}
          <div>
            <label className="text-xs text-gray-600 mb-1 block">Motivo della visita attuale</label>
            <textarea
              value={generalAnamnesis.motivo_visita}
              onChange={(e) => setGeneralAnamnesis(prev => ({ ...prev, motivo_visita: e.target.value }))}
              placeholder="Descrivi brevemente il motivo per cui hai prenotato questa visita..."
              rows={2}
              className="w-full p-2 rounded-lg border text-sm resize-none"
              style={{ borderColor: '#E5E7EB' }}
            />
          </div>
        </div>
      </div>

      {/* Sezione "Il Paziente Aggiunge" - Note personali */}
      <div className="rounded-xl p-4" style={{ background: '#FEF3C7', border: '2px solid #F59E0B' }}>
        <h3 className="text-sm font-semibold text-amber-800 mb-3 flex items-center gap-2">
          <FileText className="w-4 h-4" /> 📝 Il Paziente Aggiunge
        </h3>
        <p className="text-xs text-amber-700 mb-2">
          Usa questo spazio per aggiungere qualsiasi informazione che ritieni importante e che non è stata coperta nelle sezioni precedenti.
        </p>
        <textarea
          value={generalAnamnesis.note_paziente}
          onChange={(e) => setGeneralAnamnesis(prev => ({ ...prev, note_paziente: e.target.value }))}
          placeholder="Es: Ho avuto un episodio di visione offuscata la settimana scorsa, vedo mosche volanti da qualche mese, ho fastidio alla luce forte..."
          rows={4}
          className="w-full p-3 rounded-lg border text-sm resize-none"
          style={{ borderColor: '#F59E0B', background: 'white' }}
        />
      </div>

      {/* Sezione Upload Documentazione */}
      <div className="rounded-xl p-4" style={{ background: '#EDE9FE', border: '2px solid #8B5CF6' }}>
        <h3 className="text-sm font-semibold text-purple-800 mb-3 flex items-center gap-2">
          📎 Carica Documentazione Medica
        </h3>
        <p className="text-xs text-purple-700 mb-3">
          Puoi allegare documenti utili come: ultimo campo visivo, risonanza magnetica, ricetta occhiali, referti di visite precedenti, esami del sangue, ecc.
        </p>
        
        <div className="space-y-3">
          <label className="flex items-center justify-center w-full p-4 border-2 border-dashed rounded-xl cursor-pointer transition-all hover:border-purple-400 hover:bg-purple-50"
            style={{ borderColor: '#A78BFA' }}>
            <input
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              onChange={async (e) => {
                const files = Array.from(e.target.files);
                const token = localStorage.getItem('token');
                
                for (const file of files) {
                  try {
                    const formData = new FormData();
                    formData.append('file', file);
                    formData.append('description', '');
                    
                    const response = await fetch(`${API}/api/chat/upload-document`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}` },
                      body: formData
                    });
                    
                    if (response.ok) {
                      const data = await response.json();
                      setGeneralAnamnesis(prev => ({
                        ...prev,
                        documenti_allegati: [...(prev.documenti_allegati || []), {
                          id: data.id,
                          name: file.name,
                          size: file.size,
                          ai_summary: data.ai_summary
                        }]
                      }));
                      toast.success(`${file.name} caricato!`);
                    } else {
                      toast.error(`Errore caricamento ${file.name}`);
                    }
                  } catch (err) {
                    toast.error(`Errore caricamento ${file.name}`);
                  }
                }
              }}
              className="hidden"
            />
            <div className="text-center">
              <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-purple-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-sm font-medium text-purple-700">Clicca per caricare file</span>
              <p className="text-xs text-purple-500 mt-1">PDF, JPG, PNG, DOC (max 10MB per file)</p>
            </div>
          </label>

          {/* Lista file caricati con riassunto IA */}
          {generalAnamnesis.documenti_allegati && generalAnamnesis.documenti_allegati.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-purple-800">Documenti caricati:</p>
              {generalAnamnesis.documenti_allegati.map((doc, idx) => (
                <div key={idx} className="p-3 bg-white rounded-lg border border-purple-200 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-purple-500" />
                      <span className="text-sm font-medium text-gray-700 truncate max-w-[180px]">{doc.name}</span>
                    </div>
                    <button
                      onClick={() => {
                        setGeneralAnamnesis(prev => ({
                          ...prev,
                          documenti_allegati: prev.documenti_allegati.filter((_, i) => i !== idx)
                        }));
                      }}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      ✕
                    </button>
                  </div>
                  {doc.ai_summary && (
                    <div className="text-xs text-gray-600 p-2 bg-purple-50 rounded-lg italic">
                      🤖 <strong>Riassunto IA:</strong> {doc.ai_summary}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // ==================== RENDER STEP 5 (SINTOMI) ====================
  const renderStep5 = () => {
    const acuitaSymptoms = OCULAR_SYMPTOMS.filter(s => s.category === 'acuita');
    const visionSymptoms = OCULAR_SYMPTOMS.filter(s => s.category === 'visione');
    const comfortSymptoms = OCULAR_SYMPTOMS.filter(s => s.category === 'comfort');
    const fatigueSymptoms = OCULAR_SYMPTOMS.filter(s => s.category === 'affaticamento');
    
    const hasAcuitaSelection = acuitaSymptoms.some(s => symptoms.includes(s.id));
    const hasVisionSelection = visionSymptoms.some(s => symptoms.includes(s.id));
    const hasComfortSelection = comfortSymptoms.some(s => symptoms.includes(s.id));
    const hasFatigueSelection = fatigueSymptoms.some(s => symptoms.includes(s.id));

    // Component for symptom button with info icon
    const SymptomButton = ({ symptom, colorClass, bgColor }) => {
      const [showInfo, setShowInfo] = useState(false);
      const isSelected = symptoms.includes(symptom.id);
      
      return (
        <div className="relative">
          <button
            onClick={() => toggleSymptom(symptom.id)}
            className={`w-full p-2.5 rounded-lg text-left text-xs font-medium transition-all flex items-center justify-between gap-1 ${
              isSelected ? `${bgColor} text-white` : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <span className="flex-1">{symptom.label}</span>
            <span 
              onClick={(e) => { e.stopPropagation(); setShowInfo(!showInfo); }}
              className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold cursor-pointer transition-all ${
                isSelected ? 'bg-white/30 hover:bg-white/50' : 'bg-gray-300 hover:bg-gray-400 text-gray-600'
              }`}
              title="Clicca per info"
            >
              ℹ️
            </span>
          </button>
          {showInfo && (
            <div 
              className="absolute z-50 left-0 right-0 mt-1 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl"
              style={{ minWidth: '280px' }}
            >
              <button 
                onClick={() => setShowInfo(false)}
                className="absolute top-1 right-1 text-gray-400 hover:text-white"
              >
                ✕
              </button>
              <div className="whitespace-pre-wrap pr-4">{symptom.description}</div>
            </div>
          )}
        </div>
      );
    };
    
    return (
      <div className="space-y-4 max-h-[420px] overflow-y-auto pr-1">
        <p className="text-sm text-center text-gray-500 mb-3">
          Seleziona i sintomi che stai riscontrando. Clicca su <span className="inline-flex items-center justify-center w-4 h-4 bg-gray-300 rounded-full text-[8px]">ℹ️</span> per maggiori informazioni.
        </p>

        {/* Acuità Visiva - Difficoltà a vedere */}
        <div 
          className="rounded-xl overflow-hidden border transition-all"
          style={{ borderColor: hasAcuitaSelection ? '#8B5CF6' : '#E5E7EB' }}
        >
          <div 
            className="px-3 py-2.5 flex items-center gap-2"
            style={{ background: hasAcuitaSelection ? '#EDE9FE' : '#F9FAFB' }}
          >
            <Eye className="w-4 h-4" style={{ color: '#8B5CF6' }} />
            <h3 className="font-semibold text-sm" style={{ color: '#8B5CF6' }}>👓 Problemi di Vista (Acuità Visiva)</h3>
            {hasAcuitaSelection && (
              <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-purple-500 text-white">
                {acuitaSymptoms.filter(s => symptoms.includes(s.id)).length}
              </span>
            )}
          </div>
          <div className="p-3 bg-white">
            <div className="grid grid-cols-2 gap-2">
              {acuitaSymptoms.map(symptom => (
                <SymptomButton key={symptom.id} symptom={symptom} bgColor="bg-purple-500" />
              ))}
            </div>
          </div>
        </div>

        {/* Disturbi della Visione */}
        <div 
          className="rounded-xl overflow-hidden border transition-all"
          style={{ borderColor: hasVisionSelection ? '#3B82F6' : '#E5E7EB' }}
        >
          <div 
            className="px-3 py-2.5 flex items-center gap-2"
            style={{ background: hasVisionSelection ? '#DBEAFE' : '#F9FAFB' }}
          >
            <AlertTriangle className="w-4 h-4" style={{ color: '#3B82F6' }} />
            <h3 className="font-semibold text-sm" style={{ color: '#3B82F6' }}>👁️ Disturbi della Visione</h3>
            {hasVisionSelection && (
              <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-blue-500 text-white">
                {visionSymptoms.filter(s => symptoms.includes(s.id)).length}
              </span>
            )}
          </div>
          <div className="p-3 bg-white">
            <div className="grid grid-cols-2 gap-2">
              {visionSymptoms.map(symptom => (
                <SymptomButton key={symptom.id} symptom={symptom} bgColor="bg-blue-500" />
              ))}
            </div>
          </div>
        </div>

        {/* Sintomi di Comfort/Superficie */}
        <div 
          className="rounded-xl overflow-hidden border transition-all"
          style={{ borderColor: hasComfortSelection ? '#10B981' : '#E5E7EB' }}
        >
          <div 
            className="px-3 py-2.5 flex items-center gap-2"
            style={{ background: hasComfortSelection ? '#D1FAE5' : '#F9FAFB' }}
          >
            <Heart className="w-4 h-4" style={{ color: '#10B981' }} />
            <h3 className="font-semibold text-sm" style={{ color: '#10B981' }}>💧 Comfort e Superficie Oculare</h3>
            {hasComfortSelection && (
              <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-green-500 text-white">
                {comfortSymptoms.filter(s => symptoms.includes(s.id)).length}
              </span>
            )}
          </div>
          <div className="p-3 bg-white">
            <div className="grid grid-cols-2 gap-2">
              {comfortSymptoms.map(symptom => (
                <SymptomButton key={symptom.id} symptom={symptom} bgColor="bg-green-500" />
              ))}
            </div>
          </div>
        </div>

        {/* Affaticamento Visivo */}
        <div 
          className="rounded-xl overflow-hidden border transition-all"
          style={{ borderColor: hasFatigueSelection ? '#F59E0B' : '#E5E7EB' }}
        >
          <div 
            className="px-3 py-2.5 flex items-center gap-2"
            style={{ background: hasFatigueSelection ? '#FEF3C7' : '#F9FAFB' }}
          >
            <Clock className="w-4 h-4" style={{ color: '#F59E0B' }} />
            <h3 className="font-semibold text-sm" style={{ color: '#F59E0B' }}>😫 Affaticamento Visivo</h3>
            {hasFatigueSelection && (
              <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-amber-500 text-white">
                {fatigueSymptoms.filter(s => symptoms.includes(s.id)).length}
              </span>
            )}
          </div>
          <div className="p-3 bg-white">
            <div className="grid grid-cols-2 gap-2">
              {fatigueSymptoms.map(symptom => (
                <SymptomButton key={symptom.id} symptom={symptom} bgColor="bg-amber-500" />
              ))}
            </div>
          </div>
        </div>

        {/* Note sui sintomi */}
        {symptoms.length > 0 && (
          <div className="rounded-xl p-4" style={{ background: '#F9FAFB', border: '1px solid #E5E7EB' }}>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">📝 Descrivi meglio i tuoi sintomi</h3>
            <p className="text-xs text-gray-500 mb-2">
              Da quanto tempo li avverti? Sono peggiorati? In quali situazioni si manifestano?
            </p>
            <textarea
              value={symptomNotes}
              onChange={(e) => setSymptomNotes(e.target.value)}
              placeholder="Es: Le mosche volanti le noto da circa 2 mesi, soprattutto guardando superfici chiare. La fotofobia è peggiorata nelle ultime settimane..."
              rows={3}
              className="w-full p-2 rounded-lg border text-sm resize-none"
              style={{ borderColor: '#E5E7EB' }}
            />
          </div>
        )}
      </div>
    );
  };

  const CurrentStepIcon = stepTitles[step - 1].icon;

  return (
    <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full mx-auto overflow-hidden max-h-[90vh] flex flex-col">
      {/* Header */}
      <div className="p-4 border-b flex-shrink-0" style={{ background: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)' }}>
        <h1 className="text-lg font-bold text-white text-center">Compila la tua Anamnesi</h1>
        <p className="text-xs text-white/80 text-center mt-0.5">Step {step} di {totalSteps}</p>
      </div>

      {/* Step Indicator */}
      <div className="p-3 border-b flex-shrink-0" style={{ background: '#F9FAFB' }}>
        {renderStepIndicator()}
        <div className="text-center mt-2">
          <div className="flex items-center justify-center gap-2">
            <CurrentStepIcon className="w-5 h-5" style={{ color: '#2A9FCB' }} />
            <h2 className="font-semibold" style={{ color: '#2A9FCB' }}>{stepTitles[step - 1].title}</h2>
          </div>
          <p className="text-xs text-gray-500">{stepTitles[step - 1].subtitle}</p>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 flex-1 overflow-y-auto">
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
        {step === 5 && renderStep5()}
      </div>

      {/* Footer Navigation */}
      <div className="p-3 border-t flex justify-between gap-3 flex-shrink-0" style={{ background: '#F9FAFB' }}>
        {step > 1 ? (
          <button
            onClick={() => setStep(s => s - 1)}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg font-medium transition-all text-sm"
            style={{ background: '#E5E7EB', color: '#374151' }}
          >
            <ChevronLeft className="w-4 h-4" />
            Indietro
          </button>
        ) : (
          <button
            onClick={onCancel}
            className="px-4 py-2.5 rounded-lg font-medium transition-all text-sm"
            style={{ background: '#E5E7EB', color: '#374151' }}
          >
            Annulla
          </button>
        )}

        {step < totalSteps ? (
          <button
            onClick={() => setStep(s => s + 1)}
            className="flex items-center gap-1.5 px-5 py-2.5 rounded-lg font-medium text-white transition-all hover:opacity-90 text-sm"
            style={{ background: '#2A9FCB' }}
          >
            Avanti
            <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-5 py-2.5 rounded-lg font-medium text-white transition-all hover:opacity-90 text-sm"
            style={{ background: saving ? '#9EC5B5' : '#2A9FCB' }}
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Salvataggio...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Salva Anamnesi
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default GuidedAnamnesis;
